import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import DashboardLayout from '../components/DashboardLayout';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import AttendanceDayView   from './AttendanceDayView';
import AttendanceWeekView  from './AttendanceWeekView';
import AttendanceMonthView from './AttendanceMonthView';
import AttendanceToolbar   from './AttendanceToolbar';

// ── Exported date helpers (used by child views + tests) ───────────────────────

export const WEEKDAY_SHORT = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
export const MONTH_NAMES   = [
  'januari','februari','mars','april','maj','juni',
  'juli','augusti','september','oktober','november','december',
];

export const fmtDate = (d) => {
  const y   = d.getFullYear();
  const m   = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const getMonday = (d) => {
  const copy = new Date(d);
  const dow  = copy.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  copy.setDate(copy.getDate() + diff);
  return copy;
};

export const getWeekDates = (d) => {
  const mon = getMonday(d);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(mon);
    day.setDate(mon.getDate() + i);
    return day;
  });
};

export const getDaysInMonth = (year, month) =>
  new Date(year, month + 1, 0).getDate();

const getWeekNumber = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

const getDayLabel = (d) => {
  const wd = WEEKDAY_SHORT[d.getDay() === 0 ? 6 : d.getDay() - 1];
  return `${wd} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
};

const getWeekLabel = (d) => {
  const dates = getWeekDates(d);
  const mon = dates[0];
  const sun = dates[6];
  const wNum = getWeekNumber(mon);
  const f = (dt) => `${dt.getDate()} ${MONTH_NAMES[dt.getMonth()].slice(0, 3)}`;
  return `v.${wNum} (${f(mon)}–${f(sun)})`;
};

const getMonthLabel = (d) => {
  const name = MONTH_NAMES[d.getMonth()];
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${d.getFullYear()}`;
};

const buildAttendanceMap = (records) => {
  const map = {};
  records.forEach(r => {
    const dateKey = r.date ? r.date.split('T')[0] : '';
    if (dateKey) map[`${r.participant_id}_${dateKey}`] = { id: r.id, status: r.status, note: r.notes || '' };
  });
  return map;
};

const cycleStatus = (current) => {
  if (!current)           return 'present';
  if (current === 'present') return 'absent';
  return null;
};

// ── Confirm dialog ────────────────────────────────────────────────────────────

const AttendanceConfirmDialog = ({ open, message, confirmLabel, onConfirm, onCancel }) => {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.25)' }}
      data-testid="attendance-confirm-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid="attendance-confirm-dialog"
        className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-5"
      >
        <p className="text-base font-medium" style={{ color: '#333333' }} data-testid="confirm-message">
          {message}
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            data-testid="confirm-cancel-btn"
            className="rounded-full"
          >
            Avbryt
          </Button>
          <Button
            type="button"
            data-testid="confirm-ok-btn"
            onClick={onConfirm}
            className="rounded-full font-bold"
            style={{ background: '#F9CF3F', color: '#333333' }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const AttendancePage = () => {
  const { user } = useAuth();

  const [view, setView]               = useState('day');
  const [mode, setMode]               = useState('overview');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [participants, setParticipants] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceMap, setAttendanceMap]   = useState({});
  const [pendingEdits, setPendingEdits]     = useState({});
  const [selected, setSelected]             = useState(new Set());
  const [loading, setLoading]               = useState(true);
  const [confirmDialog, setConfirmDialog]   = useState({
    open: false, message: '', confirmLabel: 'Bekräfta', onConfirm: null,
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [attendanceRes, participantsRes] = await Promise.all([
        api.get('/attendance'),
        api.get('/participants'),
      ]);
      const records = attendanceRes.data || [];
      setAttendanceRecords(records);
      setAttendanceMap(buildAttendanceMap(records));
      setParticipants(participantsRes.data || []);
    } catch {
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  };

  // ── Effective status / note ───────────────────────────────────────────────

  const getEffectiveStatus = useCallback((pid, dateStr) => {
    const key = `${pid}_${dateStr}`;
    if (key in pendingEdits) return pendingEdits[key].status;
    return attendanceMap[key]?.status ?? null;
  }, [pendingEdits, attendanceMap]);

  const getEffectiveNote = useCallback((pid, dateStr) => {
    const key = `${pid}_${dateStr}`;
    if (key in pendingEdits) return pendingEdits[key].note;
    return attendanceMap[key]?.note ?? '';
  }, [pendingEdits, attendanceMap]);

  // ── Date navigation ───────────────────────────────────────────────────────

  const navigate = (dir) => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (view === 'day')   d.setDate(d.getDate() + dir);
      if (view === 'week')  d.setDate(d.getDate() + dir * 7);
      if (view === 'month') d.setMonth(d.getMonth() + dir);
      return d;
    });
  };

  const periodLabel = () => {
    if (view === 'day')   return getDayLabel(currentDate);
    if (view === 'week')  return getWeekLabel(currentDate);
    return getMonthLabel(currentDate);
  };

  // ── Confirm dialog helpers ────────────────────────────────────────────────

  const openConfirm = (message, onConfirm, confirmLabel = 'Bekräfta') => {
    setConfirmDialog({ open: true, message, onConfirm, confirmLabel });
  };

  const closeConfirm = () => {
    setConfirmDialog(prev => ({ ...prev, open: false, onConfirm: null }));
  };

  // ── Mode switch ───────────────────────────────────────────────────────────

  const enterRegisterMode = () => {
    setPendingEdits({});
    setSelected(new Set());
    setMode('register');
  };

  const exitRegisterMode = () => {
    if (Object.keys(pendingEdits).length > 0) {
      openConfirm(
        'Du har osparade ändringar. Vill du avsluta?',
        () => {
          setPendingEdits({});
          setSelected(new Set());
          setMode('overview');
          closeConfirm();
        },
        'Avsluta'
      );
      return;
    }
    setPendingEdits({});
    setSelected(new Set());
    setMode('overview');
  };

  // ── Selection ─────────────────────────────────────────────────────────────

  const toggleSelect = (pid) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(pid) ? next.delete(pid) : next.add(pid);
      return next;
    });
  };

  const selectAll = (allSelected) => {
    setSelected(allSelected ? new Set(participants.map(p => p.id)) : new Set());
  };

  // ── Pending edits ─────────────────────────────────────────────────────────

  const onStatusChange = (pid, dateStr, status) => {
    const key = `${pid}_${dateStr}`;
    setPendingEdits(prev => ({
      ...prev,
      [key]: { status: status || null, note: prev[key]?.note ?? attendanceMap[key]?.note ?? '' },
    }));
  };

  const onNoteChange = (pid, dateStr, note) => {
    const key = `${pid}_${dateStr}`;
    setPendingEdits(prev => ({
      ...prev,
      [key]: { status: prev[key]?.status ?? attendanceMap[key]?.status ?? null, note },
    }));
  };

  const onCellClick = (pid, dateStr) => {
    const current = getEffectiveStatus(pid, dateStr);
    onStatusChange(pid, dateStr, cycleStatus(current));
  };

  // ── Batch toolbar actions ─────────────────────────────────────────────────

  const getPeriodDates = () => {
    if (view === 'day')   return [fmtDate(currentDate)];
    if (view === 'week')  return getWeekDates(currentDate).map(fmtDate);
    const days = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
    return Array.from({ length: days }, (_, i) =>
      fmtDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1))
    );
  };

  const applyBatchStatus = (status) => {
    const dates = getPeriodDates();
    setPendingEdits(prev => {
      const next = { ...prev };
      selected.forEach(pid => {
        dates.forEach(dateStr => {
          const key = `${pid}_${dateStr}`;
          next[key] = { status, note: next[key]?.note ?? attendanceMap[key]?.note ?? '' };
        });
      });
      return next;
    });
  };

  const handleRegister = async () => {
    const dates = getPeriodDates();
    const toSave = [];
    selected.forEach(pid => {
      dates.forEach(dateStr => {
        const key    = `${pid}_${dateStr}`;
        const status = pendingEdits[key]?.status ?? attendanceMap[key]?.status ?? null;
        if (status) toSave.push({ pid, dateStr, status, note: pendingEdits[key]?.note ?? '' });
      });
    });

    if (toSave.length === 0) {
      toast.error('Inget att registrera för markerade deltagare');
      return;
    }

    const count = new Set(toSave.map(r => r.pid)).size;
    openConfirm(
      `Du är på väg att registrera närvaro för ${count} person${count !== 1 ? 'er' : ''}. Vill du fortsätta?`,
      async () => {
        closeConfirm();
        await doRegister(toSave);
      },
      'Registrera'
    );
  };

  const doRegister = async (toSave) => {
    try {
      // Optimistic update
      setAttendanceMap(prev => {
        const next = { ...prev };
        toSave.forEach(({ pid, dateStr, status, note }) => {
          next[`${pid}_${dateStr}`] = { status, note };
        });
        return next;
      });

      await Promise.all(
        toSave.map(({ pid, dateStr, status, note }) =>
          api.post('/attendance', {
            org_id:         user.org_id || 'default',
            participant_id: pid,
            date:           new Date(dateStr).toISOString(),
            status,
            notes:          note || null,
          })
        )
      );

      // Remove saved keys from pendingEdits
      setPendingEdits(prev => {
        const next = { ...prev };
        toSave.forEach(({ pid, dateStr }) => delete next[`${pid}_${dateStr}`]);
        return next;
      });
      setSelected(new Set());
      toast.success('Närvaro registrerad!');
    } catch (error) {
      // Roll back optimistic update on error
      toast.error(error.response?.data?.detail || 'Kunde inte registrera närvaro');
      fetchData();
    }
  };

  const handleClear = () => {
    setSelected(new Set());
  };

  // ── Month: navigate to day on calendar click ──────────────────────────────

  const handleDayClick = (date) => {
    setCurrentDate(date);
    setView('day');
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const weekDates = getWeekDates(currentDate);

  return (
    <DashboardLayout>
      <div className="space-y-0" data-testid="attendance-page">

        {/* Header */}
        <div className="mb-4">
          <h1 className="text-4xl font-heading font-black tracking-tight">Närvaro</h1>
          <p className="text-muted-foreground mt-1">Registrera och följ upp närvaro</p>
        </div>

        {/* Topbar */}
        <div
          className="flex flex-wrap items-center gap-3 px-4 py-3 bg-white border rounded-t-lg"
          data-testid="attendance-topbar"
        >
          {/* Date navigation */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="nav-prev"
              onClick={() => navigate(-1)}
              className="p-1.5 rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Föregående"
            >
              <ChevronLeft size={18} />
            </button>
            <span
              data-testid="period-label"
              className="text-sm font-bold min-w-[160px] text-center"
              style={{ color: '#333333' }}
            >
              {periodLabel()}
            </span>
            <button
              type="button"
              data-testid="nav-next"
              onClick={() => navigate(1)}
              className="p-1.5 rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Nästa"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* View switcher */}
          <div className="flex rounded-full overflow-hidden border" role="group" aria-label="Välj vy">
            {[
              { key: 'day',   label: 'Dag'   },
              { key: 'week',  label: 'Vecka' },
              { key: 'month', label: 'Månad' },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                data-testid={`view-${key}`}
                onClick={() => { setView(key); setSelected(new Set()); }}
                className="px-4 py-1.5 text-sm font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
                style={
                  view === key
                    ? { background: '#F9CF3F', color: '#333333' }
                    : { background: 'transparent', color: '#333333' }
                }
                aria-pressed={view === key}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Mode toggle */}
          <div className="ml-auto">
            <Button
              type="button"
              data-testid="mode-toggle"
              onClick={mode === 'overview' ? enterRegisterMode : exitRegisterMode}
              variant={mode === 'register' ? 'default' : 'outline'}
              size="sm"
              className="rounded-full font-bold"
              style={mode === 'register' ? { background: '#F9CF3F', color: '#333333' } : {}}
            >
              {mode === 'overview' ? 'Registreringsläge' : 'Översiktsläge'}
            </Button>
          </div>
        </div>

        {/* Toolbar (register mode only) */}
        {mode === 'register' && (
          <AttendanceToolbar
            selectedCount={selected.size}
            onPresent={() => applyBatchStatus('present')}
            onAbsent={() => applyBatchStatus('absent')}
            onRegister={handleRegister}
            onClear={handleClear}
            onExit={exitRegisterMode}
          />
        )}

        {/* View content */}
        <div className="bg-white border border-t-0 rounded-b-lg overflow-hidden">
          {loading ? (
            <p className="p-8 text-center text-muted-foreground">Laddar närvaro...</p>
          ) : (
            <>
              {view === 'day' && (
                <AttendanceDayView
                  participants={participants}
                  getEffectiveStatus={getEffectiveStatus}
                  getEffectiveNote={getEffectiveNote}
                  mode={mode}
                  selected={selected}
                  onToggleSelect={toggleSelect}
                  onSelectAll={selectAll}
                  onStatusChange={onStatusChange}
                  onNoteChange={onNoteChange}
                  date={fmtDate(currentDate)}
                />
              )}
              {view === 'week' && (
                <AttendanceWeekView
                  participants={participants}
                  weekDates={weekDates}
                  getEffectiveStatus={getEffectiveStatus}
                  mode={mode}
                  selected={selected}
                  onToggleSelect={toggleSelect}
                  onCellClick={onCellClick}
                />
              )}
              {view === 'month' && (
                <AttendanceMonthView
                  participants={participants}
                  attendanceRecords={attendanceRecords}
                  getEffectiveStatus={getEffectiveStatus}
                  year={currentDate.getFullYear()}
                  month={currentDate.getMonth()}
                  mode={mode}
                  selected={selected}
                  onToggleSelect={toggleSelect}
                  onCellClick={onCellClick}
                  onDayClick={handleDayClick}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Confirm dialog (replaces window.confirm) ── */}
      <AttendanceConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
    </DashboardLayout>
  );
};

export default AttendancePage;
